// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@conklin/XDL/types";
import { color } from "@conklin/XDL/Utilities";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: none;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "common.ui9",
        hover: "common.ui10",
        text: "common.ui1",
        focus: "common.ui11",
    },
    secondary: {
        main: "common.ui9",
        hover: "common.ui10",
        text: "common.ui9",
        focus: "common.ui11",
    },
};


export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        mergeCss: true,
        color: buttonColors.primary.main,
        buttonType: "solid",
        typographyProps: {
            weight: "bold",
            color: buttonColors.primary.text,
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid;
            background: ${color(buttonColors.primary.main)};
            border-color: ${color(buttonColors.primary.main)};

            &:not(:disabled):hover {
                background: ${color(buttonColors.primary.hover)};
                border-color: ${color(buttonColors.primary.hover)};
            }
            &:not(:disabled):focus {
                outline: none;
                background-color: ${color(buttonColors.primary.focus)};
                border-color: ${color(buttonColors.primary.focus)};
            }
            &:disabled {
                background-color: ${color(buttonColors.primary.main)};
                border-color: ${color(buttonColors.primary.main)};
                opacity: 0.33;
            }
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "outline",
        mergeCss: true,
        typographyProps: {
            weight: "bold",
            color: buttonColors.secondary.text,
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;

            &:not(:disabled):hover {
                color: ${color(buttonColors.secondary.hover)};
                border-color: ${color(buttonColors.secondary.hover)};
                ${TypographyStyle} {
                    color: ${color(buttonColors.secondary.hover)};
                }
            }
            &:not(:disabled):focus {
                outline: none;
                color: ${color(buttonColors.secondary.focus)};
                border-color: ${color(buttonColors.secondary.focus)};
                ${TypographyStyle} {
                    color: ${color(buttonColors.secondary.focus)};
                }
            }
            &:disabled {
                color: ${color(buttonColors.secondary.main)};
                border-color: ${color(buttonColors.secondary.main)};
                opacity: 0.33;
            }
        `,
    },
} as xBaseTheme["button"];
